import './CommerceScraper.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdSearch, MdUploadFile } from 'react-icons/md';
import { Appearance, Background, Placement, Size } from '@src/Components/designConstants';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { fetchWithRetry, poll } from '@src/utils/fetch-utils';
import { artificialWait } from '@src/utils/async';
import { Checkbox } from '@Components/FormCheck';
import { Button } from '../../Components/Button';
import { Dropdown } from '../../Components/Dropdown';
import { Page } from '../../Components/Page';
import { SegmentedControlButton } from '../../Components/SegmentedControlButton';
import { useSetError } from '../../redux/modals';
import { useExperimentFlag } from '../../utils/experiment-context';
import { ScraperResults } from './ScraperResults';
import { ScorecardPanel } from './scorecard/ScorecardPanel';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { PAGE_NAMES } from '../../Components/Page/page-constants';
import {
  AMAZON,
  AMAZON_NAVS,
  AMAZON_SEARCH_SORT_OPTIONS,
  AMAZON_TLDS,
  AmazonTab,
  BatchJob,
  createAmazonProductCollection,
  fetchAmazonProduct,
  fetchAmazonReviews,
  fetchAmazonSearch,
  fetchWalmartProduct,
  fetchWalmartReviews,
  fetchWalmartSearch,
  fetchWalmartCategory,
  getAmazonProductCollectionResults,
  getAmazonProductCollectionStatus,
  getInputLabel,
  getInputPlaceholder,
  isAmazonProductBatch,
  isMultiInput,
  parseIds,
  Platform,
  ScraperTab,
  usesIdInput,
  WALMART,
  WALMART_NAVS,
  WALMART_REVIEW_SORT_OPTIONS,
  WALMART_TLDS,
  WalmartTab
} from './CommerceScraperUtils';

const COLLECTION_POLL_INTERVAL_MS = 4000;
const COLLECTION_POLL_TIMEOUT_S = 15 * 60;

const PLATFORM_OPTIONS = [
  { key: AMAZON, label: 'Amazon' },
  { key: WALMART, label: 'Walmart' }
] as const;

const CommerceScraper = () => {
  const setError = useSetError();
  const enableCommercePdpScorecard = Boolean(useExperimentFlag('commercePdpScorecard'));

  // Platform & tab state
  const [platform, setPlatform] = useState<Platform>(AMAZON);
  const [amazonTab, setAmazonTab] = useState<AmazonTab>(AmazonTab.PRODUCT);
  const [walmartTab, setWalmartTab] = useState<WalmartTab>(WalmartTab.PRODUCT);
  const activeTab: ScraperTab = platform === AMAZON ? amazonTab : walmartTab;

  // Input state
  const [inputText, setInputText] = useState('');
  const [tld, setTld] = useState('com');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('');
  const [starFilters, setStarFilters] = useState<number[]>([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  // Results state
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<unknown | null>(null);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [batchJob, setBatchJob] = useState<BatchJob | null>(null);
  const [inputCollapsed, setInputCollapsed] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);

  // Reset results when switching platform/tab
  useEffect(() => {
    abortRef.current = true;
    setResults(null);
    setResultsError(null);
    setBatchJob(null);
    setInputText('');
    setPage(1);
    setSort('');
    setStarFilters([]);
    setVerifiedOnly(false);
    setInputCollapsed(false);
    setUploadedFileName(null);
    setLoading(false);
  }, [platform, activeTab]);

  const tldOptions = useMemo(() => (platform === AMAZON ? AMAZON_TLDS : WALMART_TLDS), [platform]);

  const navs = useMemo(() => {
    if (platform !== AMAZON) {
      return WALMART_NAVS;
    }
    return enableCommercePdpScorecard
      ? AMAZON_NAVS
      : AMAZON_NAVS.filter(nav => nav.key !== AmazonTab.SCORECARD);
  }, [platform, enableCommercePdpScorecard]);

  const handlePlatformChange = useCallback((key: Platform) => {
    setPlatform(key);
    setTld('com');
  }, []);

  const handleTabChange = useCallback(
    (key: string) => {
      if (platform === AMAZON) {
        setAmazonTab(key as AmazonTab);
      } else {
        setWalmartTab(key as WalmartTab);
      }
      Mixpanel.track(MxE.TabChange, { page: PAGE_NAMES.COMMERCE_SCRAPER, option_selected: key });
    },
    [platform]
  );

  const isBatchMode = useMemo(
    () => usesIdInput(activeTab) && isMultiInput(inputText),
    [activeTab, inputText]
  );

  // ── Single Lookup ─────────────────────────────────────────────────

  const executeSingleLookup = useCallback(async () => {
    const trimmed = inputText.trim();
    if (!trimmed) {
      return;
    }

    abortRef.current = false;
    setLoading(true);
    setResults(null);
    setResultsError(null);
    setBatchJob(null);

    try {
      let data: unknown;
      if (platform === AMAZON) {
        switch (amazonTab) {
          case AmazonTab.PRODUCT:
            data = await fetchAmazonProduct(trimmed, tld);
            break;
          case AmazonTab.SEARCH:
            data = await fetchAmazonSearch(trimmed, tld, page, sort);
            break;
          case AmazonTab.REVIEWS:
            data = await fetchAmazonReviews(trimmed, tld);
            break;
          default:
            break;
        }
      } else {
        switch (walmartTab) {
          case WalmartTab.PRODUCT:
            data = await fetchWalmartProduct(trimmed, tld);
            break;
          case WalmartTab.SEARCH:
            data = await fetchWalmartSearch(trimmed, tld, page);
            break;
          case WalmartTab.CATEGORY:
            data = await fetchWalmartCategory(trimmed, tld, page);
            break;
          case WalmartTab.REVIEWS:
            data = await fetchWalmartReviews(trimmed, tld, sort, starFilters, verifiedOnly);
            break;
          default:
            break;
        }
      }
      if (abortRef.current) {
        return;
      }
      setResults(data);
      if (usesIdInput(activeTab)) {
        setInputCollapsed(true);
      }
      Mixpanel.track(MxE.ActionTriggered, {
        page: PAGE_NAMES.COMMERCE_SCRAPER,
        action: 'single_lookup'
      });
    } catch (err) {
      if (abortRef.current) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Request failed';
      setResultsError(message);
      setError({ message: `Scraper error: ${message}`, reportError: err });
    } finally {
      if (!abortRef.current) {
        setLoading(false);
      }
    }
  }, [
    inputText,
    platform,
    amazonTab,
    walmartTab,
    activeTab,
    tld,
    page,
    sort,
    starFilters,
    verifiedOnly,
    setError
  ]);

  // ── Batch Lookup (client-side parallel) ─────────────────────────────

  const fetchSingleById = useCallback(
    (id: string): Promise<Record<string, unknown>> => {
      if (platform === AMAZON) {
        switch (amazonTab) {
          case AmazonTab.PRODUCT:
            return fetchAmazonProduct(id, tld);
          case AmazonTab.REVIEWS:
            return fetchAmazonReviews(id, tld);
          default:
            return fetchAmazonProduct(id, tld);
        }
      } else {
        switch (walmartTab) {
          case WalmartTab.PRODUCT:
            return fetchWalmartProduct(id, tld);
          case WalmartTab.REVIEWS:
            return fetchWalmartReviews(id, tld, sort, starFilters, verifiedOnly);
          default:
            return fetchWalmartProduct(id, tld);
        }
      }
    },
    [platform, amazonTab, walmartTab, tld, sort, starFilters, verifiedOnly]
  );

  // Collections only expose whole-batch idle/queued/running, so every item shows running until
  // the batch finishes.
  const executeAmazonProductCollectionBatch = useCallback(
    async (ids: string[]) => {
      abortRef.current = false;
      setLoading(true);
      setResults(null);
      setResultsError(null);

      const job: BatchJob = {
        id: 'local',
        type: activeTab,
        platform,
        status: 'running',
        total: ids.length,
        completed: 0,
        items: ids.map(id => ({ label: id, status: 'running' as const }))
      };
      setBatchJob({ ...job });

      try {
        const { collectionId } = await createAmazonProductCollection(ids, tld);

        await poll({
          period: COLLECTION_POLL_INTERVAL_MS,
          timeout: COLLECTION_POLL_TIMEOUT_S,
          fetchMaker: async () => {
            if (abortRef.current) {
              throw new Error('aborted');
            }
            const status = await getAmazonProductCollectionStatus(collectionId);
            if (status.status === 'failed' || status.status === 'unknown') {
              throw new Error(`Collection ${collectionId} ended with status "${status.status}"`);
            }
            if (status.status === 'idle' && status.resultSetIds.length > 0) {
              return status;
            }
            return false;
          }
        });
        if (abortRef.current) {
          return;
        }

        const collectionResults = await getAmazonProductCollectionResults(collectionId);
        const resultByAsin = new Map(
          collectionResults.map(result => [String(result.asin ?? '').toUpperCase(), result])
        );
        job.items = job.items.map(item => {
          const result = resultByAsin.get(item.label.toUpperCase());
          return result
            ? { ...item, status: 'done' as const, result }
            : { ...item, status: 'failed' as const, error: 'No result returned' };
        });
        job.completed = job.items.length;
        job.status = 'complete';
        setBatchJob({ ...job });
        setResults(collectionResults);
        setInputCollapsed(true);
        Mixpanel.track(MxE.ActionTriggered, {
          page: PAGE_NAMES.COMMERCE_SCRAPER,
          action: 'batch_lookup'
        });
      } catch (err) {
        if (abortRef.current) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Batch lookup failed';
        setResultsError(message);
        setError({ message: `Scraper error: ${message}`, reportError: err });
      } finally {
        if (!abortRef.current) {
          setLoading(false);
        }
      }
    },
    [activeTab, platform, tld, setError]
  );

  // ── Batch Lookup (client-side parallel — everything else) ────────────

  const executeBatchLookup = useCallback(async () => {
    const ids = parseIds(inputText);
    if (ids.length < 2) {
      return;
    }

    if (isAmazonProductBatch(platform, activeTab)) {
      await executeAmazonProductCollectionBatch(ids);
      return;
    }

    abortRef.current = false;
    setLoading(true);
    setResults(null);
    setResultsError(null);

    const items: BatchJob['items'] = ids.map(id => ({
      label: id,
      status: 'pending' as const
    }));
    const job: BatchJob = {
      id: 'local',
      type: activeTab,
      platform,
      status: 'running',
      total: ids.length,
      completed: 0,
      items
    };
    setBatchJob({ ...job });

    const completedResults: Record<string, unknown>[] = [];
    const CONCURRENCY = 2;

    const processId = async (index: number) => {
      if (abortRef.current) {
        return;
      }
      job.items[index].status = 'running';
      setBatchJob({ ...job, completed: job.completed });
      try {
        const result = await fetchWithRetry(() => fetchSingleById(ids[index]), {
          retries: 3,
          backoffMs: 2000,
          backoffMultiplier: 2
        });
        if (abortRef.current) {
          return;
        }
        job.items[index].status = 'done';
        job.items[index].result = result;
        completedResults.push(result);
      } catch (err) {
        if (abortRef.current) {
          return;
        }
        job.items[index].status = 'failed';
        job.items[index].error = err instanceof Error ? err.message : 'Failed';
      }
      job.completed += 1;
      setBatchJob({ ...job, completed: job.completed });
    };

    const INTER_BATCH_DELAY_MS = 1500;
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      if (abortRef.current) {
        break;
      }
      if (i > 0) {
        await artificialWait(INTER_BATCH_DELAY_MS);
      }
      const batch = ids.slice(i, i + CONCURRENCY).map((_, j) => processId(i + j));
      await Promise.all(batch);
    }

    if (abortRef.current) {
      return;
    }
    job.status = 'complete';
    setBatchJob({ ...job });
    setResults(completedResults);
    setInputCollapsed(true);
    setLoading(false);
    Mixpanel.track(MxE.ActionTriggered, {
      page: PAGE_NAMES.COMMERCE_SCRAPER,
      action: 'batch_lookup'
    });
  }, [inputText, platform, activeTab, fetchSingleById, executeAmazonProductCollectionBatch]);

  // ── Submit ────────────────────────────────────────────────────────

  const handleSubmit = useCallback(() => {
    if (isBatchMode) {
      executeBatchLookup();
    } else {
      executeSingleLookup();
    }
  }, [isBatchMode, executeBatchLookup, executeSingleLookup]);

  // ── File Upload ───────────────────────────────────────────────────

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target?.result;
      if (typeof text === 'string') {
        // Extract IDs from each line, handling URLs and CSVs
        const lines = text.split(/[\n\r]+/).filter(Boolean);
        const ids = lines.map(line => {
          // Try to extract ASIN from Amazon URL
          const amazonMatch = line.match(/\/dp\/([A-Z0-9]{10})/i);
          if (amazonMatch) {
            return amazonMatch[1];
          }
          // Try to extract Walmart product ID from URL
          const walmartMatch = line.match(/\/ip\/[^/]*\/(\d+)/);
          if (walmartMatch) {
            return walmartMatch[1];
          }
          // Otherwise just take the first non-empty token
          return line.split(',')[0].trim();
        });
        setInputText(ids.filter(Boolean).join('\n'));
        setUploadedFileName(file.name);
        Mixpanel.track(MxE.ActionTriggered, {
          page: PAGE_NAMES.COMMERCE_SCRAPER,
          action: 'file_upload'
        });
      }
    };
    reader.readAsText(file);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleExpandInput = useCallback(() => {
    setInputCollapsed(false);
  }, []);

  // ── Star Filter Toggle ────────────────────────────────────────────

  const toggleStarFilter = useCallback((star: number) => {
    setStarFilters(prev => (prev.includes(star) ? prev.filter(s => s !== star) : [...prev, star]));
  }, []);

  // ── Render ────────────────────────────────────────────────────────

  const showSortDropdown =
    (platform === AMAZON && activeTab === AmazonTab.SEARCH) ||
    (platform === WALMART && activeTab === WalmartTab.REVIEWS);

  const showPageInput = activeTab === 'search' || activeTab === 'category';

  const showStarFilters = platform === WALMART && activeTab === WalmartTab.REVIEWS;

  const showVerifiedCheckbox = platform === WALMART && activeTab === WalmartTab.REVIEWS;

  const sortOptions = useMemo(() => {
    if (platform === AMAZON && activeTab === AmazonTab.SEARCH) {
      return AMAZON_SEARCH_SORT_OPTIONS;
    }
    if (platform === WALMART && activeTab === WalmartTab.REVIEWS) {
      return WALMART_REVIEW_SORT_OPTIONS;
    }
    return [];
  }, [platform, activeTab]);

  const inputCount = useMemo(() => {
    if (!usesIdInput(activeTab)) {
      return 0;
    }
    return parseIds(inputText).length;
  }, [activeTab, inputText]);

  const isScorecardView =
    platform === AMAZON && amazonTab === AmazonTab.SCORECARD && enableCommercePdpScorecard;

  const showCollapsedInput = usesIdInput(activeTab) && inputCollapsed && inputCount > 0;

  return (
    <Page
      pageType={PAGE_NAMES.COMMERCE_SCRAPER}
      title={PAGE_NAMES.COMMERCE_SCRAPER}
      navs={navs}
      selectedNav={activeTab}
      onNav={handleTabChange}
      actions={
        isScorecardView ? undefined : (
          <SegmentedControlButton
            size={Size.LG}
            background={Background.DARK}
            selectedOption={platform}
            onChange={handlePlatformChange}
            options={PLATFORM_OPTIONS}
          />
        )
      }
    >
      <div className='commerce-scraper-page' data-testid='commerce-scraper-page'>
        {isScorecardView ? (
          <ScorecardPanel tld={tld} />
        ) : (
          <>
            <div className='commerce-scraper-input-panel' data-testid='scraper-input-panel'>
              {showCollapsedInput ? (
                <div
                  className='scraper-input-group scraper-input-collapsed'
                  data-testid='scraper-input-collapsed'
                >
                  <span className='scraper-input-chip'>
                    {inputCount} {inputCount === 1 ? 'ID' : 'IDs'} loaded
                    {uploadedFileName ? ` · ${uploadedFileName}` : ''}
                  </span>
                  <Button
                    appearance={Appearance.OUTLINED}
                    size={Size.SM}
                    onClick={handleExpandInput}
                    data-testid='scraper-input-change'
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <div className='scraper-input-group'>
                    <label className='scraper-label' htmlFor='scraper-input'>
                      {getInputLabel(platform, activeTab)}
                    </label>
                    {usesIdInput(activeTab) ? (
                      <textarea
                        id='scraper-input'
                        data-testid='scraper-input'
                        className='scraper-textarea'
                        placeholder={getInputPlaceholder(platform, activeTab)}
                        value={inputText}
                        onChange={e => setInputText(e.target.value)}
                        rows={6}
                      />
                    ) : (
                      <input
                        id='scraper-input'
                        data-testid='scraper-input'
                        className='scraper-text-input'
                        type='text'
                        placeholder={getInputPlaceholder(platform, activeTab)}
                        value={inputText}
                        onChange={e => setInputText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            handleSubmit();
                          }
                        }}
                      />
                    )}
                    {usesIdInput(activeTab) && inputCount > 0 && (
                      <span className='scraper-input-count' data-testid='scraper-input-count'>
                        {inputCount} {inputCount === 1 ? 'ID' : 'IDs'}
                        {isBatchMode && ' (batch mode)'}
                      </span>
                    )}
                  </div>

                  {usesIdInput(activeTab) && (
                    <div className='scraper-input-group scraper-file-upload'>
                      <label className='scraper-upload-label' htmlFor='scraper-file-upload'>
                        <MdUploadFile /> Upload file (.csv, .txt)
                        <input
                          id='scraper-file-upload'
                          ref={fileInputRef}
                          type='file'
                          accept='.csv,.txt'
                          onChange={handleFileUpload}
                          className='scraper-file-hidden'
                        />
                      </label>
                    </div>
                  )}
                </>
              )}

              <div className='scraper-input-group'>
                <span className='scraper-label'>Marketplace</span>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  value={tld}
                  options={tldOptions.map(t => ({ value: t.value, label: t.label }))}
                  onChange={setTld}
                />
              </div>

              {showPageInput && (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label className='scraper-input-group'>
                  <span className='scraper-label'>Page</span>
                  <input
                    className='scraper-text-input scraper-page-input'
                    type='number'
                    min={1}
                    value={page}
                    onChange={e => setPage(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  />
                </label>
              )}

              {showSortDropdown && (
                <div className='scraper-input-group'>
                  <span className='scraper-label'>Sort</span>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    value={sort}
                    options={sortOptions.map(s => ({ value: s.value, label: s.label }))}
                    onChange={setSort}
                  />
                </div>
              )}

              {showStarFilters && (
                <div className='scraper-input-group'>
                  <span className='scraper-label'>Star Rating</span>
                  <div className='scraper-star-filters'>
                    {[5, 4, 3, 2, 1].map(star => (
                      <Checkbox
                        key={star}
                        className='scraper-star-filter'
                        label={conditionallyPluralizeWithCount(star, 'star')}
                        checked={starFilters.includes(star)}
                        onChange={() => toggleStarFilter(star)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {showVerifiedCheckbox && (
                <div className='scraper-input-group scraper-verified-check'>
                  <Checkbox
                    checked={verifiedOnly}
                    onChange={() => setVerifiedOnly(!verifiedOnly)}
                    label='Verified purchases only'
                  />
                </div>
              )}

              <Button
                size={Size.LG}
                data-testid='scraper-submit'
                onClick={handleSubmit}
                disabled={loading || !inputText.trim()}
                loading={loading}
                leadingElement={<MdSearch />}
              >
                {isBatchMode ? 'Run Batch' : 'Look Up'}
              </Button>
            </div>

            <ScraperResults
              loading={loading}
              data={results}
              batchJob={batchJob}
              error={resultsError}
              platform={platform}
              tab={activeTab}
            />
          </>
        )}
      </div>
    </Page>
  );
};

export { CommerceScraper };
